import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { courseList } from '@/mocks/courses';

const courses = courseList.slice(0, 4);

export default function EducationSection() {
  const [activeTab, setActiveTab] = useState(0);
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section id="education" className="py-20 bg-gradient-to-br from-amber-50 to-rose-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-12">
          {/* Left Image */}
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className="w-full lg:w-[480px] flex-shrink-0 transition-all duration-800"
            style={{ opacity: leftVisible ? 1 : 0, transform: leftVisible ? 'translateX(0)' : 'translateX(-40px)', transitionDuration: '800ms' }}
          >
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-white border border-amber-200 rounded-full px-4 py-1.5 text-xs font-semibold text-amber-600 mb-4">
                <i className="ri-book-open-line"></i> 情感教育 · Education First
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/57de96b5e9dbf4eabde7a122befbca8b.jpg"
                  alt="情感教育"
                  className="w-full h-72 object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white rounded-xl p-4 shadow-lg max-w-[180px]">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-graduation-cap-fill text-red-500"></i>
                  <span className="text-xs font-bold text-gray-800">教育优先</span>
                </div>
                <p className="text-xs text-gray-500">学历认证是我们匹配系统的核心指标</p>
              </div>
            </div>
          </div>

          {/* Right Content */}
          <div
            ref={rightRef as React.RefObject<HTMLDivElement>}
            className="flex-1 transition-all duration-800"
            style={{ opacity: rightVisible ? 1 : 0, transform: rightVisible ? 'translateX(0)' : 'translateX(40px)', transitionDuration: '800ms', transitionDelay: '150ms' }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" style={{ fontFamily: "'Noto Serif SC', serif" }}>
              专业情感指导<br />
              <span className="text-red-600">婚姻教育课程</span><br />
              <span className="text-xl font-medium text-gray-500">Professional Relationship Guidance</span>
            </h2>
            {/* Price highlight */}
            <div className="inline-flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3 mb-5">
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-600">¥2</div>
                <div className="text-xs text-amber-500">象征性收费</div>
              </div>
              <div className="w-px h-8 bg-amber-200"></div>
              <p className="text-xs text-amber-700 max-w-xs leading-relaxed">
                所有课程几乎免费，仅收¥2是为了让您更珍惜这份用心准备的内容，让知识真正改变您的感情生活。
              </p>
            </div>
            <p className="text-gray-500 text-sm mb-6 max-w-lg">
              富缘佳旺提供专业的情感教育课程，帮助您在寻找另一半的同时，不断提升自我。
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {courses.map((course, i) => (
                <div
                  key={course.id}
                  onClick={() => setActiveTab(i)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${activeTab === i ? 'border-red-400 bg-white shadow-sm' : 'border-transparent bg-white/60 hover:bg-white'}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${course.color}`}>
                      <i className={`${course.icon} text-lg`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-sm font-bold text-gray-800">{course.title}</h4>
                        <span className="text-rose-600 font-bold text-sm flex-shrink-0">¥{course.price}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{course.desc}</p>
                      <div className="flex gap-3 mt-2 text-xs text-gray-400">
                        <span><i className="ri-play-circle-line mr-1"></i>{course.lessons} 课时</span>
                        <span><i className="ri-user-line mr-1"></i>{course.students.toLocaleString()}+ 学员</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <Link to="/education" className="px-6 py-3 bg-gray-800 text-white rounded-full font-semibold text-sm hover:bg-gray-900 transition-all cursor-pointer whitespace-nowrap">
                探索全部课程 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
              <Link to="/education" className="px-6 py-3 border border-gray-300 text-gray-600 rounded-full font-semibold text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-play-circle-line mr-1"></i> ¥1 立即报名
              </Link>
            </div>
          </div>
        </div>

        {/* Bottom Stats */}
        <div
          ref={statsRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-12 border-t border-amber-100"
        >
          {[
            { num: '200+', label: '专业课程', icon: 'ri-book-2-line' },
            { num: '50+', label: '认证讲师', icon: 'ri-user-star-line' },
            { num: '10万+', label: '学员受益', icon: 'ri-group-line' },
            { num: '98%', label: '满意度', icon: 'ri-star-line' },
          ].map((s, i) => (
            <div
              key={i}
              className="text-center transition-all duration-700"
              style={{ opacity: statsVisible ? 1 : 0, transform: statsVisible ? 'translateY(0)' : 'translateY(30px)', transitionDelay: `${i * 100}ms` }}
            >
              <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full mx-auto mb-3 shadow-sm">
                <i className={`${s.icon} text-red-500 text-xl`}></i>
              </div>
              <div className="text-2xl font-bold text-gray-800">{s.num}</div>
              <div className="text-sm text-gray-400 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
